import React, { useMemo, useState } from "react";
import type {
  AssessmentEffort,
  AssessmentSignalStrength,
  OwnerNamedCompetitor,
  SiteAgainAssessmentReportV1,
} from "@siteagain/shared-types";

import { formatDay, intlLocale, useLocale, type Locale } from "./i18n";
import {
  customerQuestions,
  holdingBackChecks,
  leadSearchIndex,
  ownerActions,
  ownerFacts,
  ownerVerdict,
  rivalSearch,
  technicalFacts,
  type BuildState,
  type CheckStatus,
  type FactLabel,
  type HoldingBackCheck,
  type OwnerAction,
  type OwnerFact,
  type ReachState,
  type ResultKind,
  type TechnicalFact,
} from "./owner-audit-model";

/**
 * The audit a business owner reads after onboarding (ADR-0030). It answers
 * three questions in order: can customers find me, who do they find instead,
 * what should I do next. Anything that does not answer one of them sits in the
 * collapsed technical section. There is no overall score.
 */

const EN = {
  eyebrow: (host: string, day: string) => `Your website check · ${host} · ${day}`,
  build: {
    solid: "Fast and well built.",
    fast_with_issues: "Fast, but a few things hold it back.",
    slow: "Slow to load.",
    slowPhone: "Slow to load on a phone.",
    unreadable: "Google sees an empty page.",
    unknown: "",
  } satisfies Record<BuildState | "slowPhone", string>,
  reach: {
    found: "Customers can find it.",
    google_not_ai: "Customers find it on Google. AI assistants don't mention it yet.",
    partly: "Customers find it for some searches, not most.",
    name_only: "Almost nobody can find it yet.",
    invisible: "Almost nobody can find it yet, not even by name.",
    unknown: "We could not check who finds it yet.",
  } satisfies Record<ReachState, string>,
  publicData: (day: string) => `Public data: what Google and AI assistants showed on ${day} for a sample of searches. This is not your traffic.`,
  label: { good: "Good", needs_work: "Needs work", missing: "Missing", not_checked: "Not checked" } satisfies Record<FactLabel, string>,
  facts: {
    title: "Can customers find you?",
    name: "Found when people search your name?",
    offer: "Found for what you sell?",
    ai: "Mentioned by AI assistants?",
    links: "Recommended by other sites?",
    position: (position: number) => `#${position}`,
    notTop: "Not in the top 10",
    notChecked: "Not checked",
    nameDetail: (term: string | null) => term ? `Google search for “${term}”` : "No business name could be read on the site",
    searches: (found: number, tested: number) => `${found} of ${tested} search${tested === 1 ? "" : "es"}`,
    offerDetail: "In Google's top 10",
    questions: (mentioned: number, asked: number) => `${mentioned} of ${asked} question${asked === 1 ? "" : "s"}`,
    aiDetail: (assistants: number) => `${assistants} assistant${assistants === 1 ? "" : "s"} asked`,
    linking: (count: number) => `${count.toLocaleString("en-GB")} linking site${count === 1 ? "" : "s"}`,
    linksDetail: "Websites that link to yours",
  },
  rivals: {
    title: "Who customers find instead of you",
    intro: "A real search a customer would type, and the businesses Google showed. Forums, directories, website builders and public guides are left out.",
    searchesLabel: "Searches we ran",
    you: (position: number | null) => position === null ? "You: not in the top 10" : `You: #${position}`,
    named: "You named them",
    noBusiness: (count: number) => `No business made the top ${count} for this search: forums and guides answer it. A clear page can take that place.`,
    notChecked: "Not checked: no search could be run for this report.",
    leftOut: "Left out",
    kinds: { forum: "forum", directory: "directory", builder: "website builder", government: "public guide", media: "article", social: "social network", reference: "encyclopedia" } satisfies Record<Exclude<ResultKind, "business">, string>,
    captured: (day: string) => `Google, ${day}. Results change from day to day.`,
  },
  owner: {
    title: "Who do you actually lose customers to?",
    body: "Search results show who ranks, not who you compete with. Name up to five and we show where they appear in these searches.",
    domain: "Their website",
    domainPlaceholder: "rival.com",
    name: "Name (optional)",
    add: "Add",
    remove: (name: string) => `Remove ${name}`,
    save: "Save competitors",
    saving: "Saving…",
    saved: "Saved.",
    here: (position: number | null) => position === null ? "Not in the top 10 here" : `#${position} here`,
    signIn: "Sign in to name your competitors",
    full: "Five is the limit.",
  },
  questions: {
    title: "Questions customers ask that your site doesn't answer",
    intro: (count: number, answered: number) => `Google showed these ${count} question${count === 1 ? "" : "s"} next to the searches above. Your site answers ${answered} of them.`,
    today: "Your site today",
    covered: (excerpt: string, path: string) => `Answered in a section on ${path}: “${excerpt}”`,
    mentioned: (excerpt: string, path: string) => `Mentioned in passing on ${path}: “${excerpt}”`,
    notFound: (pages: number) => `Not found on the ${pages} page${pages === 1 ? "" : "s"} we read.`,
    newPage: "Page to add",
    strengthen: "Page to strengthen",
    preview: "Preview this page",
    noneShown: (searches: number) => `Google showed no “People also ask” questions for the ${searches} searches we ran.`,
    notChecked: "Not checked in this report. Newer checks read the questions Google shows next to your searches.",
    source: "From Google's “People also ask”",
  },
  holding: {
    title: "Is your site holding you back?",
    intro: "Only what costs you customers.",
    status: { pass: "OK", fail: "Problem", not_checked: "Not checked" } satisfies Record<CheckStatus, string>,
  },
  actions: {
    title: "What to do next",
    intro: (count: number) => count === 1 ? "One action, backed by this check." : `${count} actions, most useful first.`,
    none: "Nothing in this check calls for a change. That is a valid result: we only recommend what the evidence supports.",
    why: "Why",
    what: "What changes",
    know: "How we'll know it worked",
    preview: "Preview the fix",
    impact: (value: AssessmentSignalStrength) => `Impact: ${{ high: "high", medium: "medium", low: "low" }[value]}`,
    effort: (value: AssessmentEffort) => `Effort: ${{ high: "high", medium: "medium", low: "low" }[value]}`,
  },
  connect: {
    title: "See your real numbers",
    intro: "Everything above is public data from a sample of searches. Connect your accounts and the next report uses your own numbers.",
    gsc: "Google Search Console",
    gscBody: "Your real clicks and appearances on Google, for every search, not just the ones we tested.",
    bing: "Bing Webmaster Tools",
    bingBody: "Clicks from Bing, and where Copilot cites your pages.",
    analytics: "Analytics",
    analyticsBody: "Visits, including people who arrive from ChatGPT and other assistants.",
    soon: "Coming soon",
    signIn: "Sign in to connect",
  },
  technical: {
    summary: "Technical details",
    hint: "For your web developer",
    labels: {
      load: "Homepage load time", ttfb: "Time to first byte", requests: "Requests", transfer: "Page weight",
      authority: "Domain authority", referring: "Referring domains", backlinks: "Backlinks",
      rankedKeywords: "Ranked keywords", top10Keywords: "Keywords in the top 10", strikingKeywords: "Keywords ranked 11–20",
      pagesChecked: "Pages read",
      "dimension.seo": "SEO proxy score", "dimension.geo": "AI-readiness proxy score",
      "dimension.performance": "Performance proxy score", "dimension.maintainability": "Maintainability proxy score",
    } as Record<string, string>,
    none: "No technical measurement was recorded for this check.",
  },
};

type Copy = typeof EN;

const FR: Copy = {
  eyebrow: (host, day) => `Votre bilan · ${host} · ${day}`,
  build: {
    solid: "Rapide et bien construit.",
    fast_with_issues: "Rapide, mais quelques points le freinent.",
    slow: "Lent à charger.",
    slowPhone: "Lent à charger sur téléphone.",
    unreadable: "Google voit une page vide.",
    unknown: "",
  },
  reach: {
    found: "Vos clients vous trouvent.",
    google_not_ai: "Vos clients vous trouvent sur Google. Les assistants IA ne vous citent pas encore.",
    partly: "Vos clients vous trouvent sur certaines recherches, pas la plupart.",
    name_only: "Presque personne ne le trouve encore.",
    invisible: "Presque personne ne le trouve encore, même avec votre nom.",
    unknown: "Nous n'avons pas encore pu vérifier qui vous trouve.",
  },
  publicData: (day) => `Données publiques : ce que Google et les assistants IA affichaient le ${day} pour un échantillon de recherches. Ce n'est pas votre trafic.`,
  label: { good: "Bon", needs_work: "À améliorer", missing: "Absent", not_checked: "Non vérifié" },
  facts: {
    title: "Vos clients vous trouvent-ils ?",
    name: "Trouvé quand on cherche votre nom ?",
    offer: "Trouvé pour ce que vous vendez ?",
    ai: "Cité par les assistants IA ?",
    links: "Recommandé par d'autres sites ?",
    position: (position) => `n°${position}`,
    notTop: "Hors du top 10",
    notChecked: "Non vérifié",
    nameDetail: (term) => term ? `Recherche Google « ${term} »` : "Aucun nom d'entreprise lisible sur le site",
    searches: (found, tested) => `${found} sur ${tested} recherche${tested === 1 ? "" : "s"}`,
    offerDetail: "Dans le top 10 de Google",
    questions: (mentioned, asked) => `${mentioned} sur ${asked} question${asked === 1 ? "" : "s"}`,
    aiDetail: (assistants) => `${assistants} assistant${assistants === 1 ? "" : "s"} interrogé${assistants === 1 ? "" : "s"}`,
    linking: (count) => `${count.toLocaleString("fr-FR")} site${count === 1 ? "" : "s"} qui vous cite${count === 1 ? "" : "nt"}`,
    linksDetail: "Sites qui renvoient vers le vôtre",
  },
  rivals: {
    title: "Qui vos clients trouvent à votre place",
    intro: "Une vraie recherche qu'un client taperait, et les entreprises que Google affiche. Les forums, annuaires, créateurs de sites et guides publics sont écartés.",
    searchesLabel: "Recherches effectuées",
    you: (position) => position === null ? "Vous : hors du top 10" : `Vous : n°${position}`,
    named: "Cité par vous",
    noBusiness: (count) => `Aucune entreprise dans le top ${count} pour cette recherche : forums et guides y répondent. Une page claire peut prendre cette place.`,
    notChecked: "Non vérifié : aucune recherche n'a pu être lancée pour ce bilan.",
    leftOut: "Écartés",
    kinds: { forum: "forum", directory: "annuaire", builder: "créateur de sites", government: "guide public", media: "article", social: "réseau social", reference: "encyclopédie" },
    captured: (day) => `Google, ${day}. Les résultats changent d'un jour à l'autre.`,
  },
  owner: {
    title: "À qui perdez-vous vraiment des clients ?",
    body: "Les résultats montrent qui est bien classé, pas vos vrais concurrents. Citez-en jusqu'à cinq : nous montrons où ils apparaissent dans ces recherches.",
    domain: "Leur site",
    domainPlaceholder: "concurrent.fr",
    name: "Nom (facultatif)",
    add: "Ajouter",
    remove: (name) => `Retirer ${name}`,
    save: "Enregistrer",
    saving: "Enregistrement…",
    saved: "Enregistré.",
    here: (position) => position === null ? "Hors du top 10 ici" : `n°${position} ici`,
    signIn: "Connectez-vous pour citer vos concurrents",
    full: "Cinq au maximum.",
  },
  questions: {
    title: "Les questions de vos clients auxquelles votre site ne répond pas",
    intro: (count, answered) => `Google affichait ces ${count} question${count === 1 ? "" : "s"} à côté des recherches ci-dessus. Votre site répond à ${answered} d'entre elles.`,
    today: "Votre site aujourd'hui",
    covered: (excerpt, path) => `Traité dans une section de ${path} : « ${excerpt} »`,
    mentioned: (excerpt, path) => `Évoqué en passant sur ${path} : « ${excerpt} »`,
    notFound: (pages) => `Absent des ${pages} page${pages === 1 ? "" : "s"} lue${pages === 1 ? "" : "s"}.`,
    newPage: "Page à ajouter",
    strengthen: "Page à renforcer",
    preview: "Prévisualiser cette page",
    noneShown: (searches) => `Google n'affichait aucune question « Autres questions posées » pour les ${searches} recherches effectuées.`,
    notChecked: "Non vérifié dans ce bilan. Les bilans plus récents lisent les questions que Google affiche à côté de vos recherches.",
    source: "Tiré de « Autres questions posées » de Google",
  },
  holding: {
    title: "Votre site vous freine-t-il ?",
    intro: "Seulement ce qui vous coûte des clients.",
    status: { pass: "OK", fail: "Problème", not_checked: "Non vérifié" },
  },
  actions: {
    title: "Que faire maintenant",
    intro: (count) => count === 1 ? "Une action, appuyée par ce bilan." : `${count} actions, la plus utile d'abord.`,
    none: "Rien dans ce bilan ne justifie un changement. C'est un résultat valable : nous ne recommandons que ce que les preuves soutiennent.",
    why: "Pourquoi",
    what: "Ce qui change",
    know: "Comment on saura que ça marche",
    preview: "Prévisualiser la correction",
    impact: (value) => `Impact : ${{ high: "fort", medium: "moyen", low: "faible" }[value]}`,
    effort: (value) => `Effort : ${{ high: "élevé", medium: "moyen", low: "faible" }[value]}`,
  },
  connect: {
    title: "Voir vos vrais chiffres",
    intro: "Tout ce qui précède vient de données publiques, sur un échantillon de recherches. Connectez vos comptes et le prochain rapport utilise vos propres chiffres.",
    gsc: "Google Search Console",
    gscBody: "Vos vrais clics et apparitions sur Google, pour toutes les recherches, pas seulement celles testées.",
    bing: "Bing Webmaster Tools",
    bingBody: "Les clics depuis Bing, et les pages que Copilot cite.",
    analytics: "Statistiques de visite",
    analyticsBody: "Les visites, y compris celles qui arrivent depuis ChatGPT et d'autres assistants.",
    soon: "Bientôt",
    signIn: "Se connecter pour relier",
  },
  technical: {
    summary: "Détails techniques",
    hint: "Pour votre développeur web",
    labels: {
      load: "Temps de chargement de l'accueil", ttfb: "Temps de premier octet", requests: "Requêtes", transfer: "Poids de la page",
      authority: "Autorité du domaine", referring: "Domaines référents", backlinks: "Liens entrants",
      rankedKeywords: "Mots-clés classés", top10Keywords: "Mots-clés dans le top 10", strikingKeywords: "Mots-clés en position 11–20",
      pagesChecked: "Pages lues",
      "dimension.seo": "Score indicatif SEO", "dimension.geo": "Score indicatif IA",
      "dimension.performance": "Score indicatif performance", "dimension.maintainability": "Score indicatif maintenabilité",
    },
    none: "Aucune mesure technique n'a été enregistrée pour ce bilan.",
  },
};

function copyFor(locale: Locale): Copy {
  return locale === "fr" ? FR : EN;
}

export interface OwnerAuditProps {
  report: SiteAgainAssessmentReportV1;
  host: string;
  /** Competitors the owner already named; omitted when there is no site to save them to. */
  competitors?: OwnerNamedCompetitor[];
  onSaveCompetitors?: ((competitors: OwnerNamedCompetitor[]) => Promise<OwnerNamedCompetitor[]>) | undefined;
  /** Opens the rebuild preview with this fix asked for. */
  onPreviewFix(prompt: string): void;
  /** Where a visitor without an account goes to save or connect anything. */
  onSignIn?: (() => void) | undefined;
  /** The Search Console connection, rendered when the visitor owns a site. */
  connectSlot?: React.ReactNode;
  /** Inventory, full evidence and anything else only a developer needs. */
  technicalSlot?: React.ReactNode;
  banner?: React.ReactNode;
}

export function OwnerAudit({
  report,
  host,
  competitors,
  onSaveCompetitors,
  onPreviewFix,
  onSignIn,
  connectSlot,
  technicalSlot,
  banner,
}: OwnerAuditProps) {
  const locale = useLocale();
  const t = copyFor(locale);
  const day = formatDay(report.capturedAt, locale);
  const { build, reach } = ownerVerdict(report);
  const speed = report.buyerEvidence?.speed;
  const buildLine = build === "slow" && speed?.device === "mobile" ? t.build.slowPhone : t.build[build];
  const ownDomain = hostFromUrl(report.sourceUrl) || host;

  return (
    <div className="oa">
      <span className="jr-eyebrow">{t.eyebrow(host, day)}</span>
      {banner}
      <section className="oa-verdict" aria-labelledby="oa-verdict-title">
        <h1 id="oa-verdict-title">
          {buildLine && <span>{buildLine} </span>}
          <span className="oa-verdict-reach">{t.reach[reach]}</span>
        </h1>
      </section>

      <FactTiles report={report} t={t} day={day} />
      <RivalSection report={report} ownDomain={ownDomain} t={t} locale={locale} competitors={competitors} onSave={onSaveCompetitors} onSignIn={onSignIn} />
      <QuestionSection report={report} t={t} onPreviewFix={onPreviewFix} />
      <HoldingBackSection report={report} t={t} />
      <ActionSection report={report} ownDomain={ownDomain} t={t} onPreviewFix={onPreviewFix} />
      <ConnectSection t={t} connectSlot={connectSlot} onSignIn={onSignIn} />
      <TechnicalSection report={report} t={t} locale={locale}>{technicalSlot}</TechnicalSection>
    </div>
  );
}

// ── 2 · facts ──────────────────────────────────────────────────────────────

function FactTiles({ report, t, day }: { report: SiteAgainAssessmentReportV1; t: Copy; day: string }) {
  const facts = ownerFacts(report);
  return (
    <section className="oa-section" aria-labelledby="oa-facts">
      <h2 id="oa-facts" className="oa-h2">{t.facts.title}</h2>
      <div className="oa-facts">
        {facts.map((fact) => (
          <article className={`oa-fact ${fact.label}`} key={fact.key}>
            <span className="oa-fact-q">{t.facts[fact.key]}</span>
            <strong>{factValue(fact, t)}</strong>
            <small>{factDetail(fact, t)}</small>
            <span className={`oa-label ${fact.label}`}>{t.label[fact.label]}</span>
          </article>
        ))}
      </div>
      <p className="oa-note">{t.publicData(day)}</p>
    </section>
  );
}

function factValue(fact: OwnerFact, t: Copy): string {
  if (fact.label === "not_checked") return t.facts.notChecked;
  switch (fact.key) {
    case "name": return fact.position === null ? t.facts.notTop : t.facts.position(fact.position);
    case "offer": return t.facts.searches(fact.found, fact.tested);
    case "ai": return t.facts.questions(fact.mentioned, fact.asked);
    case "links": return t.facts.linking(fact.referringDomains ?? 0);
  }
}

function factDetail(fact: OwnerFact, t: Copy): string {
  switch (fact.key) {
    case "name": return t.facts.nameDetail(fact.term);
    case "offer": return t.facts.offerDetail;
    case "ai": return t.facts.aiDetail(fact.assistants);
    case "links": return t.facts.linksDetail;
  }
}

// ── 3 · who customers find instead ─────────────────────────────────────────

function RivalSection({
  report,
  ownDomain,
  t,
  locale,
  competitors,
  onSave,
  onSignIn,
}: {
  report: SiteAgainAssessmentReportV1;
  ownDomain: string;
  t: Copy;
  locale: Locale;
  competitors?: OwnerNamedCompetitor[] | undefined;
  onSave?: OwnerAuditProps["onSaveCompetitors"];
  onSignIn?: (() => void) | undefined;
}) {
  const queries = report.buyerEvidence?.queries ?? [];
  const [named, setNamed] = useState<OwnerNamedCompetitor[]>(competitors ?? []);
  const [selected, setSelected] = useState(() => leadSearchIndex(queries, ownDomain));
  const query = queries[selected] ?? queries[0];
  const search = useMemo(() => query ? rivalSearch(query, ownDomain, named) : null, [query, ownDomain, named]);

  return (
    <section className="oa-section" aria-labelledby="oa-rivals">
      <div className="oa-head">
        <h2 id="oa-rivals" className="oa-h2">{t.rivals.title}</h2>
        <p>{t.rivals.intro}</p>
      </div>
      <div className="oa-rivals">
        <div className="jr-card oa-serp">
          {!search ? (
            <p className="oa-empty">{t.rivals.notChecked}</p>
          ) : (
            <>
              {queries.length > 1 && (
                <div className="oa-tabs" role="tablist" aria-label={t.rivals.searchesLabel}>
                  {queries.map((item, index) => (
                    <button
                      key={item.query}
                      type="button"
                      role="tab"
                      aria-selected={index === selected}
                      className={index === selected ? "active" : ""}
                      onClick={() => setSelected(index)}
                    >
                      {item.query}
                    </button>
                  ))}
                </div>
              )}
              <div className="oa-serp-head">
                <span className="oa-search-box">{search.query}</span>
                <span className={`oa-label ${search.sitePosition === null ? "missing" : search.sitePosition <= 3 ? "good" : "needs_work"}`}>
                  {t.rivals.you(search.sitePosition)}
                </span>
              </div>
              {search.businesses.length === 0 ? (
                <p className="oa-empty">{t.rivals.noBusiness(search.resultCount)}</p>
              ) : (
                <ol className="oa-serp-list">
                  {search.businesses.map((row) => (
                    <li key={row.url} className={row.named ? "named" : ""}>
                      <span className="pos">{row.position}</span>
                      <div>
                        <strong>{row.title}</strong>
                        <a href={row.url} target="_blank" rel="noreferrer noopener">{row.host}</a>
                      </div>
                      {row.named && <span className="jr-chip coral">{t.rivals.named}</span>}
                    </li>
                  ))}
                </ol>
              )}
              {search.filtered.length > 0 && (
                <p className="oa-note">
                  {t.rivals.leftOut}: {search.filtered.map((item) => `${item.host} (${t.rivals.kinds[item.kind]})`).join(", ")}
                </p>
              )}
              <p className="oa-note">{t.rivals.captured(formatDay(search.capturedAt, locale))}</p>
            </>
          )}
        </div>
        <OwnerCompetitors
          t={t}
          named={named}
          positions={search?.namedPositions ?? []}
          onSave={onSave ? async (next) => { const saved = await onSave(next); setNamed(saved); return saved; } : undefined}
          onSignIn={onSignIn}
        />
      </div>
    </section>
  );
}

function OwnerCompetitors({
  t,
  named,
  positions,
  onSave,
  onSignIn,
}: {
  t: Copy;
  named: OwnerNamedCompetitor[];
  positions: Array<{ competitor: OwnerNamedCompetitor; position: number | null }>;
  onSave?: ((competitors: OwnerNamedCompetitor[]) => Promise<OwnerNamedCompetitor[]>) | undefined;
  onSignIn?: (() => void) | undefined;
}) {
  const [draft, setDraft] = useState<OwnerNamedCompetitor[]>(named);
  const [domain, setDomain] = useState("");
  const [name, setName] = useState("");
  const [state, setState] = useState<"idle" | "saving" | "saved">("idle");
  const [error, setError] = useState("");
  const dirty = JSON.stringify(draft) !== JSON.stringify(named);
  const positionOf = (competitor: OwnerNamedCompetitor) =>
    positions.find((entry) => entry.competitor.domain === competitor.domain)?.position;

  function add(event: React.FormEvent) {
    event.preventDefault();
    const cleanDomain = domain.trim();
    if (!cleanDomain || draft.length >= 5) return;
    const cleanName = name.trim();
    setDraft((current) => [...current, cleanName ? { domain: cleanDomain, name: cleanName } : { domain: cleanDomain }]);
    setDomain("");
    setName("");
    setState("idle");
  }

  async function save() {
    if (!onSave) return;
    setState("saving");
    setError("");
    try {
      setDraft(await onSave(draft));
      setState("saved");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
      setState("idle");
    }
  }

  return (
    <aside className="jr-card oa-owner" aria-labelledby="oa-owner-title">
      <h3 id="oa-owner-title">{t.owner.title}</h3>
      <p>{t.owner.body}</p>
      {!onSave ? (
        onSignIn && <button className="jr-btn ghost small" type="button" onClick={onSignIn}>{t.owner.signIn}</button>
      ) : (
        <>
          {draft.length > 0 && (
            <ul className="oa-owner-list">
              {draft.map((competitor) => {
                const position = positionOf(competitor);
                return (
                  <li key={competitor.domain}>
                    <div>
                      <strong>{competitor.name ?? competitor.domain}</strong>
                      {position !== undefined && <small>{t.owner.here(position)}</small>}
                    </div>
                    <button
                      type="button"
                      className="oa-remove"
                      aria-label={t.owner.remove(competitor.name ?? competitor.domain)}
                      onClick={() => { setDraft((current) => current.filter((item) => item !== competitor)); setState("idle"); }}
                    >×</button>
                  </li>
                );
              })}
            </ul>
          )}
          {draft.length < 5 ? (
            <form className="oa-owner-form" onSubmit={add}>
              <label>
                <span>{t.owner.domain}</span>
                <input value={domain} onChange={(event) => setDomain(event.target.value)} placeholder={t.owner.domainPlaceholder} inputMode="url" autoComplete="off" required />
              </label>
              <label>
                <span>{t.owner.name}</span>
                <input value={name} onChange={(event) => setName(event.target.value)} autoComplete="off" />
              </label>
              <button className="jr-btn ghost small" type="submit">{t.owner.add}</button>
            </form>
          ) : (
            <p className="oa-note">{t.owner.full}</p>
          )}
          {(dirty || state !== "idle") && (
            <div className="oa-owner-save">
              <button className="jr-btn small" type="button" onClick={() => void save()} disabled={!dirty || state === "saving"}>
                {state === "saving" ? t.owner.saving : t.owner.save}
              </button>
              {state === "saved" && !dirty && <span className="oa-note" role="status">{t.owner.saved}</span>}
            </div>
          )}
          {error && <p className="oa-error" role="alert">{error}</p>}
        </>
      )}
    </aside>
  );
}

// ── 4 · questions customers ask ────────────────────────────────────────────

function QuestionSection({ report, t, onPreviewFix }: { report: SiteAgainAssessmentReportV1; t: Copy; onPreviewFix(prompt: string): void }) {
  const state = customerQuestions(report);
  return (
    <section className="oa-section" aria-labelledby="oa-questions">
      <div className="oa-head">
        <h2 id="oa-questions" className="oa-h2">{t.questions.title}</h2>
        {state.status === "ready" && <p>{t.questions.intro(state.questions.length, state.answered)}</p>}
      </div>
      {state.status === "not_checked" && <div className="jr-card pad"><p className="oa-empty">{t.questions.notChecked}</p></div>}
      {state.status === "none_shown" && <div className="jr-card pad"><p className="oa-empty">{t.questions.noneShown(state.searches)}</p></div>}
      {state.status === "ready" && (
        <div className="oa-questions">
          {state.questions.map((item) => {
            const path = item.coverage.pageUrl ? pathOf(item.coverage.pageUrl) : "";
            const today = item.coverage.status === "covered"
              ? t.questions.covered(item.coverage.excerpt ?? "", path)
              : item.coverage.status === "mentioned"
                ? t.questions.mentioned(item.coverage.excerpt ?? "", path)
                : t.questions.notFound(item.coverage.pagesRead);
            return (
              <article className={`jr-card oa-question ${item.coverage.status}`} key={item.question}>
                <h3>{item.question}</h3>
                <dl>
                  <div><dt>{t.questions.today}</dt><dd>{today}</dd></div>
                  <div>
                    <dt>{item.suggestedPage.kind === "new_page" ? t.questions.newPage : t.questions.strengthen}</dt>
                    <dd><code className="oa-path">{item.suggestedPage.path}</code></dd>
                    <dd className="oa-chips">
                      <span className="jr-chip">{t.actions.impact(item.impact)}</span>
                      <span className="jr-chip">{t.actions.effort(item.effort)}</span>
                    </dd>
                  </div>
                </dl>
                <button
                  className="jr-link"
                  type="button"
                  onClick={() => onPreviewFix(questionPrompt(item.question, item.suggestedPage))}
                >{t.questions.preview} →</button>
              </article>
            );
          })}
          <p className="oa-note">{t.questions.source}</p>
        </div>
      )}
    </section>
  );
}

// ── 5 · holding back ───────────────────────────────────────────────────────

function HoldingBackSection({ report, t }: { report: SiteAgainAssessmentReportV1; t: Copy }) {
  const locale = useLocale();
  const order: Record<CheckStatus, number> = { fail: 0, pass: 1, not_checked: 2 };
  const checks = [...holdingBackChecks(report)].sort((left, right) => order[left.status] - order[right.status]);
  return (
    <section className="oa-section" aria-labelledby="oa-holding">
      <div className="oa-head">
        <h2 id="oa-holding" className="oa-h2">{t.holding.title}</h2>
        <p>{t.holding.intro}</p>
      </div>
      <ul className="jr-card oa-checks">
        {checks.map((check) => (
          <li className={check.status} key={check.key}>
            <span className={`oa-label ${check.status === "pass" ? "good" : check.status === "fail" ? "missing" : "not_checked"}`}>{t.holding.status[check.status]}</span>
            <p>{checkLine(check, locale)}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** One plain sentence per check, written as what the customer experiences. */
export function checkLine(check: HoldingBackCheck, locale: Locale): string {
  const fr = locale === "fr";
  const seconds = (value: number) => value.toLocaleString(intlLocale(locale), { maximumFractionDigits: 1 });
  switch (check.key) {
    case "speed": {
      if (check.status === "not_checked" || check.seconds === null) return fr ? "Temps de chargement : non vérifié." : "Load time: not checked.";
      const where = check.device === "mobile"
        ? (fr ? "sur un écran de téléphone (connexion rapide)" : "on a phone-sized screen (fast connection)")
        : (fr ? "dans un navigateur d'ordinateur" : "in a desktop browser");
      return check.status === "pass"
        ? (fr ? `Se charge en ${seconds(check.seconds)} s ${where}.` : `Loads in ${seconds(check.seconds)} s ${where}.`)
        : (fr ? `Met ${seconds(check.seconds)} s à se charger ${where}. Des visiteurs partent avant.` : `Takes ${seconds(check.seconds)} s to load ${where}. Visitors leave before it appears.`);
    }
    case "readable":
      if (check.status === "not_checked") return fr ? "Lisibilité du contenu par Google : non vérifiée." : "Whether Google can read your content: not checked.";
      return check.status === "pass"
        ? (fr ? "Google lit votre contenu sans exécuter de script." : "Google can read your content without running scripts.")
        : (fr ? "Google voit une page vide tant que les scripts ne tournent pas : ce que vous vendez risque de ne pas être indexé." : "Google sees an empty page until scripts run, so what you sell may never be indexed.");
    case "title_length":
      if (check.status === "not_checked") return fr ? "Titre dans Google : non vérifié." : "Your title in Google: not checked.";
      if (check.status === "pass") return fr ? "Votre titre tient entier dans les résultats Google." : "Your title fits in Google's results.";
      return check.title === null
        ? (fr ? "Votre page d'accueil n'a pas de titre : Google en invente un." : "Your homepage has no title, so Google writes one for you.")
        : (fr ? `Votre titre fait ${check.length} caractères : Google le coupe vers 60.` : `Your title is ${check.length} characters long; Google cuts it off around 60.`);
    case "google_title":
      if (check.status === "not_checked") return fr ? "Ce que Google affiche pour votre nom : non vérifié." : "What Google shows for your name: not checked.";
      return check.status === "pass"
        ? (fr ? "Google affiche votre titre actuel." : "Google shows your current title.")
        : (fr ? `Google affiche « ${check.shown} », pas votre titre actuel « ${check.actual} ».` : `Google shows “${check.shown}”, not your current title “${check.actual}”.`);
    case "descriptions":
      if (check.status === "not_checked") return fr ? "Titres et descriptions des pages : non vérifiés." : "Page titles and descriptions: not checked.";
      return check.status === "pass"
        ? (fr ? `Les ${check.pagesChecked} pages lues ont un titre et une description.` : `All ${check.pagesChecked} pages we read have a title and a description.`)
        : (fr ? `${check.pagesMissing} des ${check.pagesChecked} pages lues n'ont pas de titre ou de description : Google écrit les siens.` : `${check.pagesMissing} of the ${check.pagesChecked} pages we read lack a title or description, so Google writes its own.`);
  }
}

// ── 6 · three actions ──────────────────────────────────────────────────────

function ActionSection({ report, ownDomain, t, onPreviewFix }: { report: SiteAgainAssessmentReportV1; ownDomain: string; t: Copy; onPreviewFix(prompt: string): void }) {
  const locale = useLocale();
  const actions = ownerActions(report, ownDomain);
  return (
    <section className="oa-section" aria-labelledby="oa-actions">
      <div className="oa-head">
        <h2 id="oa-actions" className="oa-h2">{t.actions.title}</h2>
        {actions.length > 0 && <p>{t.actions.intro(actions.length)}</p>}
      </div>
      {actions.length === 0 ? (
        <div className="jr-card pad"><p className="oa-empty">{t.actions.none}</p></div>
      ) : (
        <ol className="oa-actions">
          {actions.map((action, index) => {
            const text = actionText(action, locale);
            return (
              <li className="jr-card oa-action" key={action.key}>
                <span className="rank">{index + 1}</span>
                <div>
                  <h3>{text.title}</h3>
                  <dl>
                    <div><dt>{t.actions.why}</dt><dd>{text.why}</dd></div>
                    <div><dt>{t.actions.what}</dt><dd>{text.what}</dd></div>
                    <div><dt>{t.actions.know}</dt><dd>{text.know}</dd></div>
                  </dl>
                  <div className="oa-action-foot">
                    <span className="oa-chips">
                      <span className="jr-chip">{t.actions.impact(action.impact)}</span>
                      <span className="jr-chip">{t.actions.effort(action.effort)}</span>
                    </span>
                    <button className="jr-btn small" type="button" onClick={() => onPreviewFix(text.prompt)}>{t.actions.preview} →</button>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

/** The owner-facing wording of an action, and the request the preview is built from. */
export function actionText(action: OwnerAction, locale: Locale): { title: string; why: string; what: string; know: string; prompt: string } {
  const fr = locale === "fr";
  const quote = (text: string) => fr ? `« ${text} »` : `“${text}”`;
  switch (action.key) {
    case "answer_questions": {
      const titles = action.questions.map((item) => quote(item.question)).join(fr ? ", " : ", ");
      return {
        title: fr ? "Répondre aux questions de vos clients" : "Answer the questions customers ask",
        why: fr
          ? `Google affiche ${action.total} question${action.total === 1 ? "" : "s"} à côté des recherches sur ce que vous vendez, et votre site n'y répond pas encore.`
          : `Google shows ${action.total} question${action.total === 1 ? "" : "s"} next to searches for what you sell, and your site doesn't answer ${action.total === 1 ? "it" : "them"} yet.`,
        what: fr ? `Des pages qui y répondent : ${titles}.` : `Pages that answer them: ${titles}.`,
        know: fr
          ? "Le prochain bilan relance les mêmes recherches et montre si vos pages apparaissent."
          : "The next check runs the same searches and shows whether your pages appear.",
        prompt: `Add pages that answer these customer questions: ${action.questions.map((item) => `"${item.question}" (${item.suggestedPage.path})`).join("; ")}.`,
      };
    }
    case "rank_for_search":
      return {
        title: fr ? `Être trouvé pour ${quote(action.query)}` : `Get found for ${quote(action.query)}`,
        why: action.rivals.length > 0
          ? (fr ? `Vous n'êtes pas dans le top 10 de Google pour cette recherche. ${action.rivals.join(" et ")} y sont.` : `You're not in Google's top 10 for this search. ${action.rivals.join(" and ")} ${action.rivals.length === 1 ? "is" : "are"}.`)
          : (fr ? "Vous n'êtes pas dans le top 10 de Google pour cette recherche." : "You're not in Google's top 10 for this search."),
        what: fr
          ? "Une page construite autour de cette recherche, avec les mots de vos clients, reliée depuis votre accueil."
          : "A page built around this search, in the words customers use, linked from your homepage.",
        know: fr
          ? "Votre position sur cette recherche au prochain bilan ; avec Search Console connecté, vos vraies apparitions et vos clics."
          : "Your position for this search in the next check; with Search Console connected, your real appearances and clicks.",
        prompt: `Build a page that targets the search "${action.query}" in the customers' own words, and link it from the homepage.`,
      };
    case "ai_answers":
      return {
        title: fr ? "Donner aux assistants IA de quoi vous citer" : "Give AI assistants something to quote",
        why: fr
          ? `${action.assistants} assistant${action.assistants === 1 ? "" : "s"} ${action.assistants === 1 ? "a" : "ont"} répondu à ${quote(action.prompt)} sans vous citer.`
          : `${action.assistants} assistant${action.assistants === 1 ? "" : "s"} answered ${quote(action.prompt)} without mentioning you.`,
        what: fr
          ? "Des réponses claires sur ce que vous proposez, où et pour qui, avec des données structurées que les assistants lisent."
          : "Plain answers about what you offer, where and for whom, with structured data assistants can read.",
        know: fr
          ? "Nous reposons la même question aux mêmes assistants après le changement."
          : "We ask the same assistants the same question after the change.",
        prompt: `Make the site quotable by AI assistants for the question "${action.prompt}": state clearly what the business offers, where and for whom, and add matching structured data.`,
      };
    case "fix_foundations": {
      const failing = action.failing.map((check) => checkLine(check, locale)).join(" ");
      return {
        title: fr ? "Corriger ce qui freine le site" : "Fix what's holding the site back",
        why: failing,
        what: fr
          ? "La reconstruction corrige ces points sans changer votre contenu."
          : "The rebuild fixes these without changing your content.",
        know: fr
          ? "Les mêmes vérifications passent sur votre aperçu avant toute mise en ligne."
          : "The same checks pass on your preview before anything goes live.",
        prompt: `Fix these problems: ${action.failing.map((check) => checkLine(check, "en")).join(" ")}`,
      };
    }
  }
}

// ── 7 · connect accounts ───────────────────────────────────────────────────

function ConnectSection({ t, connectSlot, onSignIn }: { t: Copy; connectSlot?: React.ReactNode; onSignIn?: (() => void) | undefined }) {
  return (
    <section className="oa-section" aria-labelledby="oa-connect">
      <div className="oa-head">
        <h2 id="oa-connect" className="oa-h2">{t.connect.title}</h2>
        <p>{t.connect.intro}</p>
      </div>
      <ul className="jr-card oa-connect">
        <li>
          <div><strong>{t.connect.gsc}</strong><p>{t.connect.gscBody}</p></div>
          {!connectSlot && onSignIn && <button className="jr-btn ghost small" type="button" onClick={onSignIn}>{t.connect.signIn}</button>}
        </li>
        <li>
          <div><strong>{t.connect.bing}</strong><p>{t.connect.bingBody}</p></div>
          <span className="jr-chip outline">{t.connect.soon}</span>
        </li>
        <li>
          <div><strong>{t.connect.analytics}</strong><p>{t.connect.analyticsBody}</p></div>
          <span className="jr-chip outline">{t.connect.soon}</span>
        </li>
      </ul>
      {connectSlot && <div className="oa-connect-slot">{connectSlot}</div>}
    </section>
  );
}

// ── 8 · technical details ──────────────────────────────────────────────────

function TechnicalSection({ report, t, locale, children }: { report: SiteAgainAssessmentReportV1; t: Copy; locale: Locale; children?: React.ReactNode }) {
  const facts = technicalFacts(report);
  return (
    <details className="oa-technical">
      <summary><strong>{t.technical.summary}</strong><span>{t.technical.hint}</span></summary>
      <div className="oa-technical-body">
        {facts.length === 0 ? (
          <p className="oa-empty">{t.technical.none}</p>
        ) : (
          <dl className="oa-tech-grid">
            {facts.map((fact) => (
              <div key={fact.key}>
                <dt>{t.technical.labels[fact.key] ?? fact.key}</dt>
                <dd>{technicalValue(fact, locale)}</dd>
              </div>
            ))}
          </dl>
        )}
        {children}
      </div>
    </details>
  );
}

function technicalValue(fact: TechnicalFact, locale: Locale): string {
  const number = (value: number, digits = 0) => value.toLocaleString(intlLocale(locale), { maximumFractionDigits: digits });
  switch (fact.unit) {
    case "seconds": return `${number(fact.value, 1)} s`;
    case "ms": return `${number(fact.value)} ms`;
    case "bytes": return fact.value >= 1_000_000 ? `${number(fact.value / 1_000_000, 1)} MB` : `${number(fact.value / 1_000)} kB`;
    case "score": return `${number(fact.value)} / 100`;
    case "count": return number(fact.value);
  }
}

// ── helpers ────────────────────────────────────────────────────────────────

function questionPrompt(question: string, page: { path: string; kind: "new_page" | "strengthen_existing" }): string {
  return page.kind === "new_page"
    ? `Add a page at ${page.path} that answers the customer question "${question}".`
    : `Strengthen the section on ${page.path} so it fully answers the customer question "${question}".`;
}

function pathOf(url: string): string {
  try {
    return new URL(url).pathname || "/";
  } catch {
    return url;
  }
}

function hostFromUrl(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
}
